import type { AxiosResponse } from "axios";
import React, { useCallback, useMemo, useState } from "react";

import Box from "veneer/Box";
import { IconButton } from "veneer/IconButton";
import Stack from "veneer/Stack";
import { CameraVideoIcon, WallpaperIcon } from "veneer/SvgIcon";
import styled from "veneer/styled";
import { Colors } from "veneer/Theme";
import Typography from "veneer/Typography";

import { useAppDispatch } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas";
import {
  TAKE_SCREEN_SHOT_DEVICE,
  TAKE_WEBCAM_SNAPSHOT_DEVICE,
} from "cortex/ui/src/redux-store/devices/actions";
import { Panel } from "cortex/ui/src/view/layout";

import { Device } from "./model";

interface ScreenshotProps {
  canTakeWebcamSnapshot?: boolean;
  device: Device;
  isChromeOS: boolean;
  online: boolean;
}

type ScreenShotApiResponse = {
  data?: {
    screenshot?: string;
    screenshotTime?: string;
    snapshot?: string;
    snapshotTime?: string;
  };
};

const ScreenshotImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "contain",
});

const ScreenshotContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "hasScreenshot",
})<{ hasScreenshot?: boolean }>(({ hasScreenshot }) => ({
  alignItems: "center",
  backgroundColor: !hasScreenshot ? Colors.blue700 : undefined,
  display: "flex",
  gap: 2,
  height: 400,
  justifyContent: "center",
  textAlign: "center",
  width: "100%",
}));

export default function ScreenshotWidget({
  canTakeWebcamSnapshot = true,
  device,
  isChromeOS,
  online,
}: ScreenshotProps) {
  const dispatch = useAppDispatch();
  const [message, setMessage] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [screenshot, setScreenshot] = useState("");
  const hasScreenshot = Boolean(screenshot?.trim());
  const actionsDisabled = refreshing || isChromeOS;

  const statusMessage = useMemo(() => {
    if (isChromeOS) {
      return "Screenshot feature not available on ChromeOS devices.";
    }

    if (!online) {
      return "Device is currently offline";
    }

    return message;
  }, [isChromeOS, online, message]);

  const takeScreenshot = useCallback(
    (id: number) =>
      createPromiseApiAction(dispatch, TAKE_SCREEN_SHOT_DEVICE, [id]),
    [dispatch],
  );

  const takeWebcamSnapshot = useCallback(
    (deviceId: string) =>
      createPromiseApiAction(dispatch, TAKE_WEBCAM_SNAPSHOT_DEVICE, [deviceId]),
    [dispatch],
  );

  const resetBeforeFetch = useCallback(() => {
    setRefreshing(true);
    setMessage("");
  }, []);

  const finishFetch = useCallback((image = "", errorMessage = "") => {
    setScreenshot(image);
    setMessage(errorMessage);
    setRefreshing(false);
  }, []);

  const handleTakeScreenshot = useCallback(async () => {
    resetBeforeFetch();

    try {
      const res = (await takeScreenshot(
        device.id,
      )) as AxiosResponse<ScreenShotApiResponse>;
      const { data = {} } = res.data;
      finishFetch(data.screenshot || "");
    } catch {
      finishFetch("", "Device screenshot is not available at the moment.");
    }
  }, [device.id, takeScreenshot, resetBeforeFetch, finishFetch]);

  const handleTakeWebcamSnapshot = useCallback(async () => {
    resetBeforeFetch();

    try {
      const res = (await takeWebcamSnapshot(
        device.device_id,
      )) as AxiosResponse<ScreenShotApiResponse>;
      const { data = {} } = res.data;
      finishFetch(data.snapshot || "");
    } catch {
      finishFetch("", "Device snapshot is not available at the moment.");
    }
  }, [device.device_id, takeWebcamSnapshot, resetBeforeFetch, finishFetch]);

  const toolbox = (
    <Stack direction="row" gap={1}>
      {canTakeWebcamSnapshot && (
        <IconButton
          color="primary"
          dataTestId="take-webcam-snapshot-btn"
          disabled={actionsDisabled}
          icon={<CameraVideoIcon />}
          onClick={handleTakeWebcamSnapshot}
          title="Take webcam snapshot"
        />
      )}
      <IconButton
        color="error"
        dataTestId="take-screenshot-btn"
        disabled={actionsDisabled}
        icon={<WallpaperIcon />}
        onClick={handleTakeScreenshot}
        title="Take screenshot"
      />
    </Stack>
  );

  return (
    <Panel title="Device Screenshot" toolbox={toolbox}>
      <ScreenshotContainer hasScreenshot={hasScreenshot}>
        {hasScreenshot ? (
          <ScreenshotImage alt="Device screenshot" src={screenshot} />
        ) : (
          <Typography color={Colors.white}>{statusMessage}</Typography>
        )}
      </ScreenshotContainer>
    </Panel>
  );
}
